import { supabase } from '../lib/supabase';
import apiClient from './client';
import { ROLE_PERMISSIONS } from '../lib/permissions';

export interface Permission {
  id: string;
  permission_name: string;
  description?: string;
  created_at?: string;
}

export interface Role {
  id: string;
  role_name: string;
  name?: string;
  description?: string;
  created_at?: string;
  permissions?: Permission[] | string[];
  permission_count?: number;
  user_count?: number;
}

const DEFAULT_ITSM_ROLES: Role[] = Object.keys(ROLE_PERMISSIONS).map((roleName, idx) => ({
  id: `builtin-role-${idx + 1}`,
  role_name: roleName,
  name: roleName,
  description: `Standard system role for ${roleName}`,
  permissions: ROLE_PERMISSIONS[roleName],
  permission_count: ROLE_PERMISSIONS[roleName].length,
  user_count: 0
}));

export const rolesApi = {
  getRoles: async (): Promise<Role[]> => {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('roles')
          .select('*')
          .order('name');

        if (!error && data && data.length > 0) {
          return data.map((r: any) => ({
            id: r.id,
            role_name: r.name || r.role_name,
            name: r.name || r.role_name,
            description: r.description || '',
            permissions: Array.isArray(r.permissions) ? r.permissions : [],
            permission_count: Array.isArray(r.permissions) ? r.permissions.length : 0,
            created_at: r.created_at
          }));
        }
      } catch {
        // Fallback to predefined ITSM roles
      }
      return DEFAULT_ITSM_ROLES;
    }

    try {
      const { data } = await apiClient.get<Role[]>('/roles');
      return data;
    } catch {
      return DEFAULT_ITSM_ROLES;
    }
  },

  getRoleById: async (id: string): Promise<Role | null> => {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('roles')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (!error && data) {
          return {
            id: data.id,
            role_name: data.name || data.role_name,
            name: data.name || data.role_name,
            description: data.description || '',
            permissions: Array.isArray(data.permissions) ? data.permissions : [],
            permission_count: Array.isArray(data.permissions) ? data.permissions.length : 0,
            created_at: data.created_at
          };
        }
      } catch {
        // Fall through
      }
      const found = DEFAULT_ITSM_ROLES.find(r => r.id === id || r.role_name.toLowerCase() === id.toLowerCase());
      if (found) return found;
    }

    try {
      const { data } = await apiClient.get<Role>(`/roles/${id}`);
      return data;
    } catch {
      return null;
    }
  },

  createRole: async (payload: { role_name: string; description?: string; permission_ids?: string[] }): Promise<Role> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('roles')
        .insert({
          name: payload.role_name.trim(),
          description: payload.description || null,
          permissions: payload.permission_ids || []
        })
        .select()
        .single();

      if (!error && data) {
        return {
          id: data.id,
          role_name: data.name,
          name: data.name,
          description: data.description,
          permissions: data.permissions || [],
          created_at: data.created_at
        };
      }
      if (error) throw error;
    }

    const { data } = await apiClient.post<Role>('/roles', payload);
    return data;
  },

  updateRole: async (id: string, updates: { role_name?: string; description?: string; permission_ids?: string[] }): Promise<Role> => {
    if (supabase) {
      const patch: any = {};
      if (updates.role_name) patch.name = updates.role_name;
      if (updates.description !== undefined) patch.description = updates.description;
      if (updates.permission_ids !== undefined) patch.permissions = updates.permission_ids;

      const { data, error } = await supabase
        .from('roles')
        .update(patch)
        .eq('id', id)
        .select()
        .single();

      if (!error && data) {
        return {
          id: data.id,
          role_name: data.name,
          name: data.name,
          description: data.description,
          permissions: data.permissions || [],
          created_at: data.created_at
        };
      }
      if (error) throw error;
    }

    const { data } = await apiClient.put<Role>(`/roles/${id}`, updates);
    return data;
  },

  deleteRole: async (id: string): Promise<{ success: boolean }> => {
    if (supabase) {
      const { error } = await supabase.from('roles').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/roles/${id}`);
    return data;
  },

  getPermissions: async (): Promise<Permission[]> => {
    const allPerms = new Set<string>();
    Object.values(ROLE_PERMISSIONS).forEach(perms => {
      perms.forEach(p => allPerms.add(p));
    });

    return Array.from(allPerms).map((p, idx) => ({
      id: `perm-${idx + 1}`,
      permission_name: p,
      description: `Permission to execute ${p}`
    }));
  },

  getUserRoles: async (userId: string): Promise<any[]> => {
    if (supabase) {
      const { data } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();

      if (data?.role) {
        return [{ role_name: data.role, name: data.role }];
      }
    }
    return [];
  },

  assignUserRole: async (userId: string, roleNameOrId: string): Promise<any> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('profiles')
        .update({ role: roleNameOrId })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    }

    const { data } = await apiClient.post<any>(`/roles/user/${userId}`, { role_id: roleNameOrId });
    return data;
  }
};
